import { useEffect, useRef } from 'react';

interface Track {
  id: string;
  previewUrl: string | null;
}

interface PreloadedAudio {
  audio: HTMLAudioElement;
  lastAccessed: number;
}

// Global preloader cache shared across all components
const globalPreloadedAudio = new Map<string, PreloadedAudio>();

export const getGlobalPreloadedAudio = (trackId: string): HTMLAudioElement | null => {
  const preloaded = globalPreloadedAudio.get(trackId);
  if (preloaded) {
    preloaded.lastAccessed = Date.now();
    console.log('✅ Using preloaded audio for:', trackId);
    return preloaded.audio;
  }
  return null;
};

export const removeGlobalPreloadedAudio = (trackId: string) => {
  const preloaded = globalPreloadedAudio.get(trackId);
  if (preloaded) {
    globalPreloadedAudio.delete(trackId);
  }
};

/**
 * Hook to preload audio for tracks to enable instant playback
 * Maintains a pool of preloaded audio elements for quick access
 */
export const useAudioPreloader = (tracks: Track[], maxPreloaded: number = 15) => {
  const preloadedAudioRef = useRef<Map<string, PreloadedAudio>>(globalPreloadedAudio);

  useEffect(() => {
    const preloadedMap = preloadedAudioRef.current;
    
    // Get tracks with preview URLs that aren't already preloaded
    const tracksToPreload = tracks
      .filter(track => track.previewUrl && !preloadedMap.has(track.id))
      .slice(0, maxPreloaded);

    // Preload new tracks
    tracksToPreload.forEach(track => {
      if (!track.previewUrl) return;

      const audio = new Audio();
      audio.preload = 'auto';
      audio.volume = 1.0;
      audio.src = track.previewUrl;
      
      // Start loading the audio immediately
      audio.load();

      preloadedMap.set(track.id, {
        audio,
        lastAccessed: Date.now()
      });

      console.log('🎵 Preloading audio for:', track.id.substring(0, 20));
    });

    // Clean up old preloaded audio if we exceed the max
    if (preloadedMap.size > maxPreloaded) {
      const entries = Array.from(preloadedMap.entries());
      entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
      
      // Remove oldest entries
      const toRemove = entries.slice(0, entries.length - maxPreloaded);
      toRemove.forEach(([id, { audio }]) => {
        audio.pause();
        audio.src = '';
        preloadedMap.delete(id);
        console.log('🧹 Cleaned up preloaded audio for:', id);
      });
    }

    // Don't cleanup on unmount - keep preloaded audio in global cache
  }, [tracks, maxPreloaded]);

  return { getPreloadedAudio: getGlobalPreloadedAudio, removePreloadedAudio: removeGlobalPreloadedAudio };
};
